import {Injectable, OnDestroy, signal} from '@angular/core';
import {CRD_DISPLAY_MODE_KEY, CrdDisplayMode, parseDisplayMode} from './crd-display';

/**
 * The display mode (CrdDisplayMode) of every screen in this browser, kept in localStorage. A screen in a window of
 * its own (pop-out) follows a change made in another window through the `storage` event. Without storage (private
 * window, blocked site data) the mode is `fit` and a change lasts until the page is left.
 */
@Injectable({providedIn: 'root'})
export class CrdDisplaySetting implements OnDestroy {
  private readonly current = signal<CrdDisplayMode>(parseDisplayMode(read()));
  readonly mode = this.current.asReadonly();

  private readonly onStorage = (event: StorageEvent) => {
    if (event.key === CRD_DISPLAY_MODE_KEY) this.current.set(parseDisplayMode(event.newValue));
  };

  constructor() {
    window.addEventListener('storage', this.onStorage);
  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.onStorage);
  }

  set(mode: CrdDisplayMode) {
    this.current.set(mode);
    try {
      localStorage.setItem(CRD_DISPLAY_MODE_KEY, mode);
    } catch {
      // no storage: the mode lasts until the page is left
    }
  }
}

function read(): string | null {
  try {
    return localStorage.getItem(CRD_DISPLAY_MODE_KEY);
  } catch {
    return null;
  }
}
